import { motion } from "framer-motion";
import { WA_LINK } from "@/data";
import { MessageCircle } from "lucide-react";

const STUDIO_IMG =
  "https://customer-assets.emergentagent.com/job_wellness-andheri/artifacts/gphuhnh8_beautify_these_2_202604131844%20%282%29.png";

const galleryCards = [
  {
    label: "Studio Gallery",
    title: "Private Studio,\nAndheri West",
    subtitle: "Fully equipped, serene & discreet — 15 mins from Mumbai Airport",
    src: STUDIO_IMG,
    overlay: "from-black/75 via-black/45 to-black/60",
    accent: "#FF4500",
  },
  {
    label: "Expert Therapy",
    title: "Professional\nPrecision",
    subtitle: "15+ years of certified massage therapy crafted exclusively for men in Mumbai",
    src: "https://customer-assets.emergentagent.com/job_wellness-andheri/artifacts/rdfzkqci_male_to_male_202604151815%20%282%29.png",
    overlay: "from-black/80 via-black/50 to-black/65",
    accent: "#FF4500",
  },
  {
    label: "Your Experience",
    title: "Total\nRelaxation",
    subtitle: "Complete mind-body restoration — studio, home & hotel service available",
    src: "https://customer-assets.emergentagent.com/job_wellness-andheri/artifacts/4rgowf7u_male_to_male_202604151815%20%284%29.png",
    overlay: "from-black/75 via-black/45 to-black/60",
    accent: "#FF4500",
    cta: true,
  },
];

function GalleryCard({ card, index }) {
  return (
    <div
      className="sticky top-0 h-[56vw] sm:h-screen flex items-center justify-center overflow-hidden"
      style={{ zIndex: index + 1 }}
      data-testid={`gallery-card-${index}`}
    >
      {/* Background Image */}
      <img
        src={card.src}
        alt={card.title.replace("\n", " ")}
        className="absolute inset-0 w-full h-full object-cover"
        loading={index === 0 ? "eager" : "lazy"}
      />

      {/* Dark overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-b ${card.overlay}`}
      />
      {/* Side vignette */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="max-w-xl"
        >
          {/* Label */}
          <span className="inline-block mb-2 sm:mb-4 text-[10px] sm:text-xs tracking-[0.25em] uppercase font-semibold font-inter px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-md text-white">
            {card.label}
          </span>

          {/* Title */}
          <h2 className="text-xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-2 sm:mb-5 whitespace-pre-line">
            {card.title.split("\n").map((line, i) =>
              i === 0 ? (
                <span key={i}>
                  {line}
                  <br />
                </span>
              ) : (
                <span key={i} style={{ color: card.accent }}>
                  {line}
                </span>
              )
            )}
          </h2>

          {/* Subtitle — hidden on mobile to preserve image space */}
          <p className="hidden sm:block text-gray-200 text-base sm:text-lg font-inter leading-relaxed mb-8 max-w-md">
            {card.subtitle}
          </p>

          {/* CTA on last card */}
          {card.cta && (
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FF4500] hover:bg-[#e63d00] text-white font-semibold px-5 py-2.5 sm:px-7 sm:py-4 rounded-full transition-all duration-200 hover:scale-105 shadow-lg shadow-[#FF4500]/30 text-sm sm:text-base"
              data-testid="gallery-cta-btn"
            >
              <MessageCircle size={16} />
              Book a Session
            </a>
          )}
        </motion.div>
      </div>

      {/* Card counter */}
      <div className="absolute bottom-3 sm:bottom-8 right-4 sm:right-8 z-10 flex items-center gap-2">
        {galleryCards.map((_, i) => (
          <span
            key={i}
            className={`block rounded-full transition-all duration-300 ${
              i === index
                ? "w-6 h-1.5 bg-[#FF4500]"
                : "w-1.5 h-1.5 bg-white/30"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function StudioGallery() {
  return (
    <section
      className="relative h-[168vw] sm:h-[300vh]"
      data-testid="studio-gallery-section"
      aria-label="Studio Gallery"
    >
      {galleryCards.map((card, i) => (
        <GalleryCard key={i} card={card} index={i} />
      ))}
    </section>
  );
}
